import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Undo2 } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { EmptyState } from '../../../components/EmptyState';
import { useAssetStore } from '../../../store/assetStore';

const fieldClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';

const CONDITIONS = ['Good', 'Fair', 'Damaged', 'Needs repair'];

/**
 * Return an assigned asset to stock (POST /assets/:id/return). Optionally
 * retire it straight after (POST /assets/:id/retire) for end-of-life returns.
 */
export function ReturnAssetView() {
  const { assets, fetchAssets, returnAsset, retireAsset, isLoading } = useAssetStore();
  const [assetId, setAssetId] = useState('');
  const [condition, setCondition] = useState('Good');
  const [afterReturn, setAfterReturn] = useState<'stock' | 'retire'>('stock');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const assigned = useMemo(() => assets.filter((a) => a.status === 'Assigned'), [assets]);
  const selected = assigned.find((a) => a.id === assetId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) { toast.error('Choose the asset being returned'); return; }
    setBusy(true);
    try {
      await returnAsset(selected.id, { condition, notes: notes || undefined });
      if (afterReturn === 'retire') {
        await retireAsset(selected.id, { reason: notes || `Returned in ${condition.toLowerCase()} condition` });
        toast.success(`${selected.asset_code} returned and retired`);
      } else {
        toast.success(`${selected.asset_code} returned to stock`);
      }
      setAssetId('');
      setNotes('');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader className="mb-0" icon={Undo2} title="Return asset" description="Take a device back from an employee and return it to stock or retire it." />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4">
          <h2 className="mb-3 font-display text-card-title text-foreground">Process a return</h2>
          <form className="space-y-3" onSubmit={submit}>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Asset</span>
              <select className={fieldClass} value={assetId} onChange={(e) => setAssetId(e.target.value)}>
                <option value="">{assigned.length ? 'Choose an assigned asset' : 'No assets are assigned'}</option>
                {assigned.map((a) => <option key={a.id} value={a.id}>{a.asset_code} — {a.name} ({a.assignedToName})</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Condition</span>
              <select className={fieldClass} value={condition} onChange={(e) => setCondition(e.target.value)}>
                {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">After return</span>
              <select className={fieldClass} value={afterReturn} onChange={(e) => setAfterReturn(e.target.value as 'stock' | 'retire')}>
                <option value="stock">Back to stock</option>
                <option value="retire">Retire (end of life)</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Notes</span>
              <textarea
                className="min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30"
                value={notes}
                maxLength={2000}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Accessories returned, damage, etc."
              />
            </label>
            <Button type="submit" className="w-full" isLoading={busy} disabled={!selected}>
              <Undo2 /> Process return
            </Button>
          </form>
        </Card>

        <Card className="lg:col-span-2">
          <div className="border-b border-border px-4 py-3">
            <h2 className="font-display text-card-title text-foreground">Currently assigned ({assigned.length})</h2>
          </div>
          {assigned.length === 0 ? (
            <EmptyState className="m-4 border-0" title={isLoading ? 'Loading…' : 'No assets are assigned'} description="Assigned devices appear here so they can be returned." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Asset</th>
                    <th className="px-4 py-2 font-semibold">Assigned to</th>
                    <th className="px-4 py-2 font-semibold">Department</th>
                    <th className="px-4 py-2 font-semibold">Since</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {assigned.map((a) => (
                    <tr key={a.id} className="hover:bg-secondary/30">
                      <td className="px-4 py-2">
                        <p className="font-medium text-foreground">{a.asset_code}</p>
                        <p className="text-xs text-muted-foreground">{a.name}</p>
                      </td>
                      <td className="px-4 py-2 text-foreground">{a.assignedToName || '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">{a.department || '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">{a.assignedOn || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="xs" variant="ghost" onClick={() => setAssetId(a.id)}>Select</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
